import React, { useState } from 'react';
import { Layout, Card, Row, Col, Typography, Spin, Statistic, DatePicker, Button, Space, message } from 'antd';
import { 
    DollarOutlined, 
    RiseOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    SearchOutlined,
    ClearOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportsPDF from './ReportsPDF';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title: AntTitle } = Typography;
const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const FinancialReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [financialData, setFinancialData] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            let queryParams = "";
            if (dateRange[0] && dateRange[1]) {
                const start = dateRange[0].format('YYYY-MM-DD');
                const end = dateRange[1].format('YYYY-MM-DD');
                queryParams = `?startDate=${start}&endDate=${end}`;
            }

            const res = await axiosClient.get(`/reports/get_financial_reports.php${queryParams}`);
            
            if (res.data.status === 'success') {
                setFinancialData(res.data.data);
                message.success("Financial report generated.");
            }
        } catch (error) {
            console.error("Error fetching financial report:", error);
            message.error("Failed to fetch report.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setDateRange([null, null]);
        setFinancialData(null);
    };

    const handleExportExcel = () => {
        if (!financialData) return;
        const wb = XLSX.utils.book_new();
        const revenueData = financialData.monthlyRevenue.map(item => ({
            Month: item.month,
            Revenue: item.total
        }));
        const wsRevenue = XLSX.utils.json_to_sheet(revenueData);
        XLSX.utils.book_append_sheet(wb, wsRevenue, "Revenue Trend");
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
        saveAs(data, "FinancialReport.xlsx");
    };

    const chartData = {
        labels: financialData?.monthlyRevenue.map(item => item.month) || [],
        datasets: [
            {
                label: 'Monthly Revenue',
                data: financialData?.monthlyRevenue.map(item => item.total) || [],
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                tension: 0.3
            }
        ]
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
                <div style={{ marginBottom: 20 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <AntTitle level={2} style={{ margin: 0 }}><DollarOutlined /> Financial Reports</AntTitle>
                    <Space>
                        <RangePicker onChange={setDateRange} value={dateRange} />
                        <Button type="primary" icon={<SearchOutlined />} onClick={fetchReport} loading={loading}>Fetch</Button>
                        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={loading}>Clear</Button>
                        
                        {financialData && (
                            <>
                                <PDFDownloadLink
                                    document={<ReportsPDF financial={financialData} dateRange={dateRange} />}
                                    fileName="FinancialReport.pdf"
                                >
                                    {({ loading: pdfLoading }) => (
                                        <Button danger icon={<FilePdfOutlined />} loading={pdfLoading}>PDF</Button>
                                    )}
                                </PDFDownloadLink>

                                <Button 
                                    onClick={handleExportExcel}
                                    style={{ borderColor: '#217346', color: '#217346' }} 
                                    icon={<FileExcelOutlined />}
                                >
                                    Excel
                                </Button>
                            </>
                        )}
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div>
                ) : financialData ? (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Total Revenue (All Time)" 
                                            value={financialData.totalRevenue} 
                                            precision={2}
                                            prefix="$" 
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Total Invoices" 
                                            value={financialData.totalInvoices} 
                                            prefix={<DollarOutlined />} 
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Card title="Revenue Trend">
                                 <Line options={{ responsive: true, plugins: { legend: { position: 'top' } } }} data={chartData} />
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <div style={{ textAlign: 'center', marginTop: 100, color: '#999' }}>
                        <DollarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>No report loaded.</p>
                    </div>
                )}
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default FinancialReport;
